/**
 * Casa o que foi lido da etiqueta com as unidades e moradores do condominio.
 *
 * Roda no cliente porque o OCR ja roda aqui: a lista de unidades e moradores
 * ja esta em memoria para montar os campos da tela, entao o palpite sai
 * instantaneo e sem mais uma ida ao servidor com o pacote na mao do porteiro.
 *
 * A regra central e nunca escolher no lugar do porteiro quando ha duvida:
 * "1102" existindo em tres blocos vira uma pergunta na tela, nao um chute. Um
 * campo preenchido errado e pior do que um campo vazio, porque o porteiro
 * confia no que ja esta preenchido.
 *
 * Duvida, porem, tem grau. Havia so dois desfechos aqui — preencher ou nao
 * achar nada — e o "nao achar nada" engolia o palpite que valia uma pergunta:
 * a etiqueta trazia "Gabriel", o predio tinha tres Gabriels, e a tela devolvia
 * silencio como se nada tivesse sido lido. Por isso existe o terceiro desfecho
 * (`ask: 'confirm'`): mostrar o palpite raso como pergunta, que custa um toque
 * e evita a digitacao inteira.
 */

import type { Resident, Unit } from '@azko/shared';
import { normalizeText, type LabelHints } from './parseLabel.js';
import { confidenceOf, nameTokens, scoreName, type NameConfidence, type NameScore } from './nameMatch.js';

export interface UnitSuggestion {
  unitId: string;
  unitLabel: string;
  block: string;
  residentId: string | null;
  residentName: string | null;
  /** De onde veio o palpite — a UI mostra isso ao porteiro. */
  reason: string;
}

export interface LabelMatch {
  /** Unica e confiavel: a tela pode preencher sozinha. */
  suggestion: UnitSuggestion | null;
  /** Compativeis demais para decidir sozinho — a tela pergunta. */
  candidates: UnitSuggestion[];
  /**
   * Como a tela deve tratar `candidates`:
   *
   * - `'pick'`: varias opcoes empatadas de verdade (o mesmo numero em blocos
   *   diferentes). A pergunta e "qual delas?".
   * - `'confirm'`: o palpite e um so, mas raso — so o primeiro nome, ou o nome
   *   inteiro com erro de leitura demais. A pergunta e "e esse mesmo?".
   * - `null`: nao ha nada a perguntar.
   */
  ask: 'pick' | 'confirm' | null;
}

/** Acima disso a pergunta vira uma lista, e uma lista o porteiro ja tem no formulario. */
const MAX_CANDIDATES = 4;

/** "0102" e "102" sao a mesma unidade em metade dos condominios. */
function normalizeLabel(value: string): string {
  const normalized = normalizeText(value).replace(/[\s.-]+/g, '');
  const withoutLeadingZeros = normalized.replace(/^0+(?=\d)/, '');
  return withoutLeadingZeros || normalized;
}

function sameLabel(a: string, b: string): boolean {
  return normalizeLabel(a) === normalizeLabel(b);
}

function describe(unit: { block: string; label: string }): string {
  return unit.block ? `${unit.block} • ${unit.label}` : unit.label;
}

function primaryLinkOf(resident: Resident) {
  return resident.units.find((link) => link.isPrimary) ?? resident.units[0] ?? null;
}

// ---------------------------------------------------------------------------
// Nome do destinatario
// ---------------------------------------------------------------------------

/**
 * De onde tirar os pedacos de nome da etiqueta.
 *
 * Sao duas leituras da mesma etiqueta, e as duas sao usadas:
 *
 * - a linha que o parser marcou como destinatario, que e precisa — ali da para
 *   cobrar que o cadastro explique TUDO o que foi lido;
 * - a etiqueta inteira, que e o resgate: quando o rotulo "DESTINATARIO" sai
 *   ilegivel o nome continua impresso em algum lugar, so nao da mais para
 *   saber o que era nome e o que era endereco.
 */
interface NameSource {
  tokens: string[];
  /** `true` na linha do destinatario. Ver `confidenceOf`. */
  scoped: boolean;
}

function nameSources(hints: LabelHints, ocrText: string): NameSource[] {
  const sources: NameSource[] = [];
  if (hints.recipient) sources.push({ tokens: nameTokens(hints.recipient), scoped: true });
  sources.push({ tokens: nameTokens(ocrText), scoped: false });
  return sources;
}

interface RankedResident {
  resident: Resident;
  score: NameScore;
  confidence: Exclude<NameConfidence, null>;
}

const TIER: Record<Exclude<NameConfidence, null>, number> = { strong: 2, weak: 1 };

function bestScoreFor(resident: Resident, sources: NameSource[]): RankedResident | null {
  let best: RankedResident | null = null;

  for (const source of sources) {
    const score = scoreName(resident.name, source.tokens);
    const confidence = confidenceOf(score, source.scoped);
    if (confidence === null) continue;

    if (!best || compareRank({ resident, score, confidence }, best) < 0) {
      best = { resident, score, confidence };
    }
  }

  return best;
}

/** Ordena do palpite mais forte para o mais fraco. Empate = duvida real. */
function compareRank(a: RankedResident, b: RankedResident): number {
  if (a.confidence !== b.confidence) return TIER[b.confidence] - TIER[a.confidence];
  if (a.score.matched !== b.score.matched) return b.score.matched - a.score.matched;
  if (a.score.fuzzy !== b.score.fuzzy) return a.score.fuzzy - b.score.fuzzy;
  return a.score.unexplained - b.score.unexplained;
}

/** Dois palpites igualmente bons: nenhum dos dois autoriza escolher sozinho. */
function tied(a: RankedResident, b: RankedResident): boolean {
  return compareRank(a, b) === 0;
}

function rankResidents(residents: Resident[], sources: NameSource[]): RankedResident[] {
  return residents
    .map((resident) => bestScoreFor(resident, sources))
    .filter((entry): entry is RankedResident => entry !== null)
    .sort(compareRank);
}

// ---------------------------------------------------------------------------
// Montagem da sugestao
// ---------------------------------------------------------------------------

function fromResident(resident: Resident, reason: string): UnitSuggestion | null {
  const link = primaryLinkOf(resident);
  if (!link) return null;

  return {
    unitId: link.unitId,
    unitLabel: link.unitLabel,
    block: link.block,
    residentId: resident.id,
    residentName: resident.name,
    reason,
  };
}

// ---------------------------------------------------------------------------
// Match
// ---------------------------------------------------------------------------

export function matchLabel(
  hints: LabelHints,
  ocrText: string,
  units: Unit[],
  residents: Resident[],
): LabelMatch {
  const activeUnits = units.filter((unit) => unit.status === 'active');

  // --- Caminho 1: numero do apartamento -----------------------------------
  let byNumber: Unit[] = [];
  if (hints.unitNumber) {
    byNumber = activeUnits.filter((unit) => sameLabel(unit.label, hints.unitNumber ?? ''));

    // O bloco so entra como filtro, nunca como criterio sozinho: em condominio
    // sem blocos ele viria vazio e zeraria o resultado.
    if (byNumber.length > 1 && hints.block) {
      const inBlock = byNumber.filter((unit) => unit.block && sameLabel(unit.block, hints.block ?? ''));
      if (inBlock.length > 0) byNumber = inBlock;
    }
  }

  // --- Caminho 2: nome do destinatario ------------------------------------
  const ranked = rankResidents(
    residents.filter((resident) => resident.status === 'active' && resident.units.length > 0),
    nameSources(hints, ocrText),
  );

  // --- Cruzamento: numero + nome concordam --------------------------------
  // O caso mais forte, e o unico em que um palpite RASO ja basta: um "Gabriel"
  // solto nao decide nada sozinho, mas um "Gabriel" que mora justamente no
  // apartamento lido na mesma etiqueta nao e coincidencia. Duas leituras
  // independentes apontando para a mesma pessoa valem mais do que qualquer
  // uma das duas sozinha.
  for (const entry of ranked) {
    const link = entry.resident.units.find((item) =>
      byNumber.some((unit) => unit.id === item.unitId),
    );
    if (!link) continue;

    return {
      suggestion: {
        unitId: link.unitId,
        unitLabel: link.unitLabel,
        block: link.block,
        residentId: entry.resident.id,
        residentName: entry.resident.name,
        reason: `apto e nome da etiqueta batem com ${entry.resident.name}`,
      },
      candidates: [],
      ask: null,
    };
  }

  // --- So o numero ---------------------------------------------------------
  if (byNumber.length === 1) {
    const unit = byNumber[0]!;
    return {
      suggestion: {
        unitId: unit.id,
        unitLabel: unit.label,
        block: unit.block,
        residentId: null,
        residentName: null,
        reason: `unidade ${describe(unit)} lida na etiqueta`,
      },
      candidates: [],
      ask: null,
    };
  }

  // Ambiguo: mesmo numero em blocos diferentes. Perguntar, nao chutar.
  if (byNumber.length > 1) {
    return {
      suggestion: null,
      candidates: byNumber.slice(0, 6).map((unit) => ({
        unitId: unit.id,
        unitLabel: unit.label,
        block: unit.block,
        residentId: null,
        residentName: null,
        reason: describe(unit),
      })),
      ask: 'pick',
    };
  }

  // --- So o nome -----------------------------------------------------------
  const best = ranked[0];
  if (!best) return { suggestion: null, candidates: [], ask: null };

  const runnerUp = ranked[1];
  const unique = !runnerUp || !tied(best, runnerUp);

  // Nome + sobrenome, sem empate: preenche. E o caso de "Gabriel Aguiar" (ou
  // "Gabriel Firmo", ou "Gabriel Aguiaar") cair no cadastro "Gabriel Pereira
  // Firmo de Aguiar" — nenhum deles traz o nome inteiro, e todos identificam.
  if (best.confidence === 'strong' && unique) {
    const suggestion = fromResident(
      best.resident,
      `destinatario ${best.resident.name} mora na unidade`,
    );
    if (suggestion) return { suggestion, candidates: [], ask: null };
  }

  // Palpite raso ou empatado: vira pergunta. Os empatados entram junto — sao
  // exatamente os xarás que o porteiro precisa desempatar, e mostrar a unidade
  // de cada um e o que deixa ele desempatar sem sair da tela.
  const asking = ranked.filter((entry) => tied(entry, best)).slice(0, MAX_CANDIDATES + 1);

  // Acima do teto o palpite deixou de ser palpite: "ANA" contra oito Anas nao
  // ajuda ninguem, e uma fileira de botoes iguais so atrapalha quem ja ia usar
  // o campo de busca do formulario.
  if (asking.length > MAX_CANDIDATES) return { suggestion: null, candidates: [], ask: null };

  const candidates = asking
    .map((entry) => {
      const link = primaryLinkOf(entry.resident);
      if (!link) return null;
      // O nome sozinho nao desempata xarás — e a unidade de cada um que faz o
      // porteiro reconhecer de quem a etiqueta esta falando.
      return fromResident(
        entry.resident,
        `${entry.resident.name} • ${describe({ block: link.block, label: link.unitLabel })}`,
      );
    })
    .filter((item): item is UnitSuggestion => item !== null);

  return {
    suggestion: null,
    candidates,
    ask: candidates.length > 0 ? (candidates.length === 1 ? 'confirm' : 'pick') : null,
  };
}
